import Loader from "Components/Loader/loader";
import React from "react";
const { ResponsiveBar, BarCanvas } = require("@nivo/bar");
import PropTypes from "prop-types";
import Modal from "Components/Modal/Modal";
import { Button } from "primereact/button";

const NivoBar = ({
  data,
  title,
  legendWidth,
  keysNames,
  indexName,
  graphHeight,
  displayPercentage,
  isStacked,
  displayFill,
  colors,
  legendXAxis,
  legendYAxis,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  // Declare the props once in a variable
  const chartProps = {
    data,
    margin: { top: 80, right: 140, bottom: 40, left: 80 },
    padding: 0.3,
    valueScale: { type: "linear" },
    indexScale: { type: "band", round: true },
    colors: { scheme: colors || "nivo" },
    keys: keysNames,
    indexBy: indexName,
    defs: [
      {
        id: "dots",
        type: "patternDots",
        background: "inherit",
        color: "#38bcb2",
        size: 4,
        padding: 1,
        stagger: true,
      },
      {
        id: "lines",
        type: "patternLines",
        background: "inherit",
        color: "#eed312",
        rotation: -45,
        lineWidth: 6,
        spacing: 10,
      },
    ],
    groupMode: isStacked ? "stacked" : "grouped",
    fill: [
      {
        match: {
          id: displayFill && keysNames[0],
        },
        id: "lines",
      },
      {
        match: {
          id: displayFill && keysNames?.at(2),
        },
        id: "dots",
      },
    ],
    borderColor: {
      from: "color",
      modifiers: [["darker", 1.6]],
    },
    axisTop: null,
    axisRight: null,
    axisBottom: {
      tickRotation: 0,
      legend: legendXAxis,
      legendPosition: "middle",
      legendOffset: 32,
    },
    axisLeft: {
      tickRotation: 0,
      legend: legendYAxis,
      legendPosition: "middle",
      legendOffset: -50,
    },
    labelSkipWidth: 12,
    labelSkipHeight: 12,
    labelTextColor: {
      from: "color",
      modifiers: [["darker", 1.6]],
    },
    layers: [
      "grid",
      "axes",
      "bars",
      "markers",
      "legends",
      "annotations",
      //? Layer pour le titre du graphique
      () => {
        return (
          <text
            //? 50% - (margin) =~ 45%
            x={"45%"}
            y={-55}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontSize: "1 rem",
              fontWeight: "bold",
              fill: "#333333",
            }}
          >
            {title}
          </text>
        );
      },
    ],
    legends: [
      {
        dataFrom: "keys",
        anchor: "bottom-right",
        direction: "column",
        justify: false,
        translateX: 120,
        translateY: 0,
        itemsSpacing: 2,
        itemWidth: legendWidth,
        itemHeight: 20,
        itemDirection: "left-to-right",
        itemOpacity: 0.85,
        symbolSize: 20,
        effects: [
          {
            on: "hover",
            style: {
              itemOpacity: 1,
            },
          },
        ],
      },
    ],
    role: "application",
    ariaLabel: "Nivo bar chart demo",
    barAriaLabel: (e) =>
      e.id + ": " + e.formattedValue + " in country: " + e.indexValue,
    label: displayPercentage
      ? ({ data, value }) => {
          //? On additionne tous les champs numériques de l'objet data
          let total = 0;

          for (const key in data) {
            if (typeof data[key] === "number") {
              total += data[key];
            }
          }

          const pourcentage =
            total !== 0 ? ((value / total) * 100).toFixed(1) : 0;

          return `${value} (${pourcentage}%)`;
        }
      : undefined,
  };

  /**
   * Exporte le graphique Bar en format .xlsx
   *
   * Avec la librairie xlsx
   */
  const exportBarChartExcel = () => {
    import("xlsx").then((xlsx) => {
      const excelData = [...data];
      excelData.map((e) => {
        e.Date = e.date;
        delete e.date;
        //? On supprime toutes les clés qui ont 'Color' dans leur nom
        for (const key in e) {
          if (key.includes("Color")) {
            delete e[key];
          }
        }
        return e;
      });

      const headers = ["Date", ...keysNames];
      const tableData = [headers];

      excelData.forEach((e) => {
        const rowData = [e["Date"]];
        headers.slice(1).forEach((category) => {
          rowData.push(e[category]);
        });
        tableData.push(rowData);
      });

      const workBook = xlsx.utils.book_new();
      const workSheet = xlsx.utils.aoa_to_sheet(tableData);

      xlsx.utils.book_append_sheet(
        workBook,
        workSheet,
        "Statistiques - Offres"
      );
      xlsx.writeFile(
        workBook,
        //? Format string to filename
        title.normalize("NFD") + ".xlsx"
      );
    });
  };

  return (
    <>
      {data ? (
        <React.Fragment>
          <div
            style={{
              height: graphHeight,
              // Marge sur les côtés
              margin: "0 10%",
            }}
          >
            <ResponsiveBar
              {...chartProps}
              onClick={() => {
                setDialogOpen(true);
              }}
            />
          </div>
          <div className="align-items-center export-buttons stats_container__component-export">
            <Button
              type="button"
              icon="pi pi-file-excel"
              onClick={exportBarChartExcel}
              className="p-button-success xl"
              data-pr-tooltip="Exporter sous format Excel"
            />
          </div>
        </React.Fragment>
      ) : (
        <Loader />
      )}
      <Modal
        header={title}
        visible={dialogOpen}
        setVisible={setDialogOpen}
        height="80vh"
      >
        <BarCanvas
          {...chartProps}
          theme={{
            background: "#FFFFFF",
          }}
          height={graphHeight}
          width={window.innerWidth - 100}
          onClick={() => {
            setDialogOpen(false);
          }}
        />
      </Modal>
    </>
  );
};

NivoBar.propTypes = {
  data: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  legendWidth: PropTypes.number,
  keysNames: PropTypes.array.isRequired,
  indexName: PropTypes.string.isRequired,
  graphHeight: PropTypes.number.isRequired,
  displayPercentage: PropTypes.bool,
  isStacked: PropTypes.bool,
  colors: PropTypes.string,
  displayFill: PropTypes.bool,
  legendXAxis: PropTypes.string,
  legendYAxis: PropTypes.string,
};

NivoBar.defaultProps = {
  legendWidth: 100,
  graphHeight: 600,
  displayPercentage: false,
  isStacked: true,
  colors: "nivo",
  displayFill: true,
  legendXAxis: "Date",
  legendYAxis: "Offres",
};

export default NivoBar;
